/**
 * 画面遷移の設定ファイル
 * @todo basename(base href)でベースのパスを指定
 */

/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useMemo } from "react";

import {
  createBrowserRouter,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";

import { ErrorBoundary } from "react-error-boundary";

import Header from "components/layouts/Header";
import Footer from "components/layouts/Footer";
import Progress from "components/layouts/Progress";

import Appointment from "screens/Appointment";
import AppointmentCalendar from "screens/AppointmentCalendar";
import KikiSelect from "screens/KikiSelect";
import Contract from "screens/Contract";
import Conf from "screens/Confirm";
import Thanks from "screens/Thanks";

import Error from "screens/Error";
import ErrorNotFound from "screens/ErrorNotFound";
import ErrorFallback from "screens/ErrorFallback";
import ContractMtgCallback from "screens/ContractMtgCallback";
import ScrollTop from "routes/ScrollTop";

import GtmHandler from "./GtmHandler";

// ベースURL
const basename = "/shch-mtmr";

// /** ベースURLへジャンプ
//  * @see https://github.com/remix-run/react-router/issues/8427#issuecomment-1056988913
//  */
if (!window.location.pathname.includes(basename)) {
  window.history.replaceState("", "", basename + window.location.pathname);
}

/**
 * Submitした遷移かを判定し、画面コンポーネントを返却
 * @param screens
 * @returns 画面コンポーネント
 */
const CheckSubmit = ({ screens }) => {
  const location = useLocation();

  const getScreens = useMemo(() => {
    let isError = false;

    // Submitした遷移の場合、画面コンポーネントを返却
    if (location.state === null || (location.state && !location.state.isSubmit)) {
      isError = true;
    }

    return isError ? (
      // 遷移エラー画面を返却
      <Navigate to="/error" state={{ errorCode: 403 }} />
    ) : (
      screens
    );
  }, [screens, location]);

  return getScreens;
};

const LayoutWrap = () => {
  return (
    <>
      <ScrollTop />
      <div css={LayoutWrapCss}>
        <Header></Header>
        <Outlet />
      </div>
      <Footer></Footer>
    </>
  );
};

const routes = [
  {
    path: "/",
    element: (
      <>
        {/* GTM */}
        {process.env.REACT_APP_GTM_CONTAINER_ID && <GtmHandler />}
        <LayoutWrap />
      </>
    ),
    children: [
      {
        path: "/",
        element: <Navigate to="/appointment" />,
      },
      // ご希望日時の選択
      {
        path: "/appointment",
        element: (
          <>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Progress step={1} />
              <Appointment />
            </ErrorBoundary>
          </>
        ),
      },
      // ご希望日時の選択（カレンダー）
      {
        path: "/appointmentCalendar",
        element: (
          <>
            <CheckSubmit
              screens={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Progress step={1} />
                  <AppointmentCalendar />
                </ErrorBoundary>
              }
            ></CheckSubmit>
          </>
        ),
      },
      // 機器の選択
      {
        path: "/kikiSelect",
        element: (
          <>
            <CheckSubmit
              screens={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Progress step={2} />
                  <KikiSelect />
                </ErrorBoundary>
              }
            ></CheckSubmit>
          </>
        ),
      },
      // お客さま情報入力
      {
        path: "/contract",
        element: (
          <>
            <CheckSubmit
              screens={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Progress step={3} />
                  <Contract />
                </ErrorBoundary>
              }
              isConf={true}
            ></CheckSubmit>
          </>
        ),
      },
      // お客さま情報入力 MTGコールバック
      {
        path: "/contract/mtg_callback",
        element: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Progress step={3} />
            <ContractMtgCallback />
          </ErrorBoundary>
        ),
      },
      // 内容確認
      {
        path: "/confirm",
        element: (
          <>
            <CheckSubmit
              screens={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Progress step={4} />
                  <Conf />
                </ErrorBoundary>
              }
            ></CheckSubmit>
          </>
        ),
      },
      // 完了
      {
        path: "/thanks",
        element: (
          <>
            <CheckSubmit
              screens={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Progress step={5} />
                  <Thanks />
                </ErrorBoundary>
              }
            ></CheckSubmit>
          </>
        ),
      },
      // エラー
      {
        path: "/error",
        element: <Error />,
      },
      // 上記に該当しない場合
      {
        path: "/*",
        element: <ErrorNotFound />,
      },
    ],
  },
];

const AppRoutes = createBrowserRouter(routes, {
  basename: basename,
});

// cssの設定
const LayoutWrapCss = css`
  margin: 0 auto;
  max-width: var(--mtg-max-width);
  padding: var(--mtg-padding-top) var(--mtg-padding-width) var(--mtg-padding-bottom);
  /* 基本の文字色 */
  color: var(--mtg-midnight);
  /* 文字の両端揃え */
  text-align: justify;
  text-justify: inter-ideograph;
`;

export default AppRoutes;
